/**
 * Error-code → copy mapping for `/subscriptions/admin/*`
 * (SUBSCRIPTIONS-FRONTEND-BRIEFING.md § 2: "Elke fout is `{"error": "<code>"}`").
 * `ApiError.reason` carries the API's `reason`/`error` field verbatim (see
 * `src/api/client.ts#readReason`) — this maps the documented codes to
 * translated copy and falls back to `SubmitError`'s generic 403/409/message
 * handling for anything else.
 */
import { useTranslation } from 'react-i18next';

import { ApiError, ConflictError, ForbiddenError } from '@/api/errors';
import { Alert } from '@/app/components/ui/Alert';

const KNOWN_CODES = [
  'unknown_provider',
  'unknown_config_key',
  'invalid_provider',
  'invalid_tier',
  'invalid_period_amount',
  'invalid_period_unit',
  'no_active_subscription',
  'purchase_not_found',
  'already_on_account',
  'invalid_user',
  'subscription_not_found',
  'subscription_cancelled',
  'mollie_unreachable',
] as const;

type KnownCode = (typeof KNOWN_CODES)[number];

function matchKnownCode(reason: string | undefined): KnownCode | undefined {
  if (!reason) return undefined;
  // `unknown_config_key: <key>` carries a dynamic suffix.
  const head = reason.split(':')[0]?.trim();
  return KNOWN_CODES.find((code) => code === head);
}

/** Renders a failed admin write as a clear sentence, per-code where the API
 * documents one, otherwise the same generic 403/409/message handling as
 * `SubmitError`. */
export function AdminSubmitError({ error }: { error: Error | null }) {
  const { t } = useTranslation();
  if (!error) return null;

  if (error instanceof ApiError) {
    const code = matchKnownCode(error.reason);
    if (code) {
      return <Alert tone="danger">{t(`manageSubscriptions.errors.${code}`)}</Alert>;
    }
  }

  const suffix = (reason: string | undefined) => (reason ? `: ${reason}` : '.');
  let message: string;
  if (error instanceof ForbiddenError) {
    message = t('errors.submitForbidden', { suffix: suffix(error.reason) });
  } else if (error instanceof ConflictError) {
    message = t('errors.submitConflict', { suffix: suffix(error.reason) });
  } else if (error instanceof ApiError) {
    message = error.message;
  } else {
    message = t('errors.submitGeneric', { message: error.message });
  }

  return <Alert tone="danger">{message}</Alert>;
}
